// Per-number detail.
//
// A DID points at three things, so the page is three cards, left to right in the
// order a call meets them:
//
//   PBX host    where the carrier delivers it
//   Subscriber  who it belongs to — and the caller ID it presents, which is the
//               subscriber's name unless this number overrides it
//   Location    where 911 sends help
//
// Each card is the same shape: an icon and an invitation when nothing is linked
// (click → a picker), a summary when something is (click → that object's own page,
// with a corner control for changing the link without navigating).
//
// There is no kebab. Every action is a control you can see: assigning is the empty
// card, changing a link is that card's corner, and the three number-level actions
// sit in the header.
//
// Deliberately absent:
//
//   * Routing. What a number rings only means anything once it has a subscriber,
//     and it's chosen from that subscriber's extensions — so it's set there.
//   * Messaging, for the same reason: SMS is a subscriber-level service.
//   * Carrier bookkeeping (Commio ids, LERG, OCN, LATA). Needed when
//     troubleshooting, never when operating, so it's behind View Carrier Record.
//
// Explanations are TOOLTIPS, not prose in the layout.
//
// Two things that look like faults and aren't, both real in this org's data: a
// number can have NO carrier routing profile (10 of 164 do), and a profile can
// belong to no PBX host at all (`LyonsCommPBX`, `teli_bridge_12059`). Neither is
// warned about; the host card simply says so.

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import useBackTo from '../hooks/useBackTo.js';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { usePbxNames } from '../hooks/usePbxNames.js';
import { useAuth } from '../hooks/useAuth.js';
import { canOperate, canAdmin } from '../lib/roles.js';
import LoadingSpinner from '../components/shared/LoadingSpinner.jsx';
import ConfirmDeleteDialog from '../components/shared/ConfirmDeleteDialog.jsx';
import CallerIdCard from '../components/lines/CallerIdCard.jsx';

// Caller ID is delivered in a 15-character field. Enforced server-side too; here
// it stops the operator typing a name that would arrive truncated.

const TIPS = {
  carrier: 'Who delivers this number, and the routing profile that decides which PBX they hand it to. A number can sit with no profile at all — that is a normal state at the carrier, not a fault.',
  host: 'The PBX the carrier hands calls to. Everything Verso builds for this number — extensions, inbound routes — is built there.',
  cnam: 'The name shown to people this number calls. Fifteen characters; carriers cut off the rest.',
  subscriber: 'Who this number belongs to. The address, caller ID and 911 record all follow from it.',
  location: 'Where 911 sends help when someone calls from this number. It has to be one of this subscriber’s addresses.',
};

function fmtDid(e164) {
  if (!e164) return '';
  const s = String(e164).replace(/[^0-9]/g, '');
  if (s.length === 11 && s[0] === '1') return `+1 (${s.slice(1, 4)}) ${s.slice(4, 7)}-${s.slice(7)}`;
  if (s.length === 10) return `(${s.slice(0, 3)}) ${s.slice(3, 6)}-${s.slice(6)}`;
  return String(e164);
}

function PersonIcon({ className = 'h-6 w-6' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function HostIcon({ className = 'h-6 w-6' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="7" rx="2" />
      <rect x="3" y="13" width="18" height="7" rx="2" />
      <path d="M7 7.5h.01M7 16.5h.01" />
    </svg>
  );
}

function PinIcon({ className = 'h-6 w-6' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function GearIcon({ className = 'h-4 w-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6 1.65 1.65 0 0 0 10 3.09V3a2 2 0 1 1 4 0v.09A1.65 1.65 0 0 0 15 4.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.14.34.4.63.73.82" />
    </svg>
  );
}

// Every card's manage control: a gear, top-right, opening that card's picker.
function GearButton({ label, onClick }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClick(); }}
      className="relative z-10 flex-shrink-0 rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
    >
      <GearIcon />
    </button>
  );
}

// How recently the agent checked in. Heartbeat is every 30s, so under two minutes
// is healthy — the same threshold the PBX hosts list uses.
function seenLabel(lastSeen) {
  if (!lastSeen) return { text: 'never seen', cls: 'text-gray-500' };
  const ts = typeof lastSeen === 'number' ? lastSeen * 1000 : Date.parse(lastSeen);
  if (!Number.isFinite(ts)) return { text: 'never seen', cls: 'text-gray-500' };
  const mins = Math.round((Date.now() - ts) / 60000);
  if (mins < 2) return { text: 'online', cls: 'text-green-700' };
  if (mins < 60) return { text: `last seen ${mins}m ago`, cls: 'text-amber-800' };
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return { text: `last seen ${hrs}h ago`, cls: 'text-red-600' };
  return { text: `last seen ${Math.round(hrs / 24)}d ago`, cls: 'text-red-600' };
}

const primaryBtn = 'rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50';
const plainBtn = 'rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50';
const dangerBtn = 'rounded-md border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400';
const pickRow = 'flex w-full items-center justify-between gap-3 rounded-md border border-gray-200 px-3 py-2 text-left text-sm hover:border-blue-400 hover:bg-blue-50/40 disabled:opacity-50';

/**
 * An object this number is linked to.
 *
 * Empty: an icon and an invitation, and the whole card is the button that opens
 * the picker. Filled: a summary, and the whole card is the way to that object's
 * page — with `onEdit` in the corner for changing the link without navigating.
 */
// The border is the verdict on this link: is it set, and is what it points at
// healthy? Green edge = checked and good; amber/red = look here; dashed grey =
// nothing linked yet. Colour only ever repeats what the card also says in words.
const TONES = {
  ok:    { border: 'border-green-300', icon: 'text-green-600' },
  warn:  { border: 'border-amber-300 bg-amber-50/40', icon: 'text-amber-600' },
  bad:   { border: 'border-red-300 bg-red-50/40', icon: 'text-red-600' },
  empty: { border: 'border-dashed border-gray-300', icon: 'text-gray-400' },
};

function LinkCard({ icon, label, tip, tone = 'empty', toneReason, filled, to, onOpen, onEdit, editLabel, openLabel, children, footer, disabledReason }) {
  const t = TONES[tone] || TONES.empty;
  // A fixed minimum keeps the three cards the same height whether or not they're
  // filled, so the row doesn't jump as a number gets set up.
  const shell = `flex w-full min-h-[8.5rem] items-start gap-4 rounded-lg border bg-white p-5 text-left transition-colors ${t.border}`;
  const head = (
    <div className="min-w-0 flex-1">
      <div title={tip} className={`text-xs font-medium uppercase tracking-wide text-gray-500 ${tip ? 'cursor-help' : ''}`}>
        {label}
      </div>
      <div className="mt-1">{children}</div>
      {/* Above the stretched link so anything interactive in here still works. */}
      {footer && <div className="relative z-10 mt-3 border-t border-gray-100 pt-2.5">{footer}</div>}
    </div>
  );

  if (!filled) {
    // Nothing linked. When it *can't* be linked yet, say why instead of offering a
    // control that would be refused.
    if (disabledReason) {
      return (
        <div className={shell} title={disabledReason}>
          <span className="text-gray-300">{icon}</span>
          {head}
        </div>
      );
    }
    return (
      <div className={`${shell} relative hover:border-blue-400 hover:bg-blue-50/30`} title={toneReason}>
        <span className={t.icon}>{icon}</span>
        {head}
        {/* The whole card is the button; the gear just shows where to look. */}
        <span aria-hidden="true" className="relative z-0 flex-shrink-0 p-1.5 text-gray-300">
          <GearIcon />
        </span>
        <button type="button" aria-label={openLabel} onClick={onOpen} className="absolute inset-0 rounded-lg" />
      </div>
    );
  }

  return (
    <div className={`${shell} relative hover:border-blue-300`} title={toneReason}>
      <span className={t.icon}>{icon}</span>
      {head}
      {onEdit && <GearButton label={editLabel} onClick={onEdit} />}
      {/* Stretched link: the card is the target, the corner button sits above it. */}
      <Link to={to} className="absolute inset-0 rounded-lg" aria-label={`Open ${label.toLowerCase()}`} />
    </div>
  );
}

// Things that are wrong, as opposed to merely unset. Amber is "look at this", red
// is "a call will go somewhere it shouldn't".
function Warn({ level = 'warn', children }) {
  const cls = level === 'bad'
    ? 'border-red-200 bg-red-50 text-red-800'
    : 'border-amber-200 bg-amber-50 text-amber-900';
  return <div className={`rounded-md border px-3 py-2 text-sm ${cls}`}>{children}</div>;
}

function Modal({ title, subtitle, onClose, children, wide = false }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className={`max-h-[85vh] w-full ${wide ? 'max-w-2xl' : 'max-w-lg'} overflow-y-auto rounded-lg bg-white p-6 shadow-xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">{title}</h3>
            {subtitle && <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p>}
          </div>
          <button className={plainBtn} onClick={onClose}>Close</button>
        </div>
        {children}
      </div>
    </div>
  );
}

const addressLine1 = (l) => l?.addressLine1 || l?.address_line1 || l?.label || (l ? `Location #${l.id}` : null);
const addressRest = (l) => [[l?.city, l?.state].filter(Boolean).join(', '), l?.postal].filter(Boolean).join(' ');
const addressLabel = (l) => {
  if (!l) return null;
  const rest = addressRest(l);
  return rest ? `${addressLine1(l)} · ${rest}` : addressLine1(l);
};

// A location can serve several subscribers (migration 0032), so membership is a
// list test, not an equality test.
const locSubscriberIds = (l) => {
  const ids = (l.subscribers || []).map((x) => Number(x.id));
  const mirror = l.subscriberId ?? l.subscriber_id;
  if (mirror != null) ids.push(Number(mirror));
  return ids;
};

/** One labelled fact in the Technical details sheet. */
function TechRow({ label, children }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-0.5 truncate text-gray-900">{children}</dd>
    </div>
  );
}

/**
 * Read a field that may arrive camelCase or snake_case, WITHOUT destroying `null`.
 *
 * `a ?? b` is the obvious spelling and it is wrong whenever null is meaningful: it falls
 * through to a key that does not exist and turns "we know it is empty" into "we do not
 * know". Presence, not truthiness, decides.
 */
function pick(obj, camel, snake) {
  if (obj && Object.prototype.hasOwnProperty.call(obj, camel)) return obj[camel];
  if (obj && Object.prototype.hasOwnProperty.call(obj, snake)) return obj[snake];
  return undefined;
}

export default function DidDetail() {
  // Where the arrow returns to — the screen the operator left, not this object's
  // global list. Falls back to the list when nothing was passed. See hooks/useBackTo.js.
  const back = useBackTo('DIDs', '/dids');
  const { pbxName, hostByRouteId } = usePbxNames();
  const { e164: e164Param } = useParams();
  const queryClient = useQueryClient();
  const { role } = useAuth();
  const canOp  = canOperate(role);
  const canAdm = canAdmin(role);

  const rawE164 = decodeURIComponent(e164Param || '');

  const didQ = useQuery({
    queryKey: ['did', rawE164],
    queryFn: () => api.get(`/dids/${encodeURIComponent(rawE164)}`),
  });

  // One thing open at a time: 'subscriber' | 'location' | 'host' | 'cnam' | 'carrier'.
  const [open, setOpen] = useState(null);
  const [subSearch, setSubSearch] = useState('');
  const [pickedPbx, setPickedPbx] = useState('');
  const [confirmUnassign, setConfirmUnassign] = useState(false);
  const [confirmClearE911, setConfirmClearE911] = useState(false);
  const [confirmInactive, setConfirmInactive] = useState(false);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['did', rawE164] });
    queryClient.invalidateQueries({ queryKey: ['dids'] });
    // The subscriber's page lists this number with its caller ID and 911 address,
    // so a change here has to show there without a reload.
    queryClient.invalidateQueries({ queryKey: ['subscriber'] });
    // And the line, which shows this number's caller ID on its own page.
    queryClient.invalidateQueries({ queryKey: ['line'] });
  };
  const close = () => { setOpen(null); setSubSearch(''); setPickedPbx(''); };

  const did = didQ.data;
  const subId = did?.subscriberId ?? did?.subscriber_id ?? null;

  // Addresses load with the page: they're the current value, the picker's choices,
  // and what the subscriber↔address cross-check is made of.
  const locsQ = useQuery({ queryKey: ['locations', 'all'], queryFn: () => api.get('/locations') });

  // WHAT THE CARRIER ACTUALLY HOLDS, asked of the carrier.
  //
  // Everything 911 on this page came from `did.commioLocationId` — OUR column — while the field is
  // labelled "Carrier 911 site" and the modal is subtitled "What Commio holds". It never asked.
  //
  // +17208934186, 2026-09-05: a build registered it for 911 at site 10802, failed at the PBX step,
  // and the line was deleted. The number went back to inventory with Verso's column null and a
  // LIVE registration at the carrier — and this page, the one an operator checks before reselling
  // a number, said "Carrier 911 site: none".
  //
  // Best-effort: an unreadable carrier leaves the page exactly as it was rather than breaking it.
  const liveQ = useQuery({
    queryKey: ['did', rawE164, 'commio-live'],
    queryFn: () => api.get(`/dids/${encodeURIComponent(rawE164)}/commio-live`),
    retry: false,
    staleTime: 30_000,
  });
  const subsQ = useQuery({
    queryKey: ['subscribers'],
    queryFn: () => api.get('/subscribers'),
    enabled: open === 'subscriber',
  });
  // Same query key usePbxNames uses, so this is served from cache: the card wants
  // the host's own details, not just its name.
  const pbxQ = useQuery({
    queryKey: ['pbx-hosts'],
    queryFn: () => api.get('/pbx-hosts'),
    staleTime: 60_000,
  });

  const assignSub = useMutation({
    mutationFn: (id) => api.put(`/dids/${encodeURIComponent(rawE164)}/subscriber/${id}`),
    onSuccess: () => { invalidate(); close(); },
  });
  const unassign = useMutation({
    mutationFn: () => api.delete(`/dids/${encodeURIComponent(rawE164)}/subscriber`),
    onSuccess: () => { invalidate(); setConfirmUnassign(false); },
  });
  // DETACH THIS NUMBER FROM ITS 911 SITE AT THE CARRIER.
  //
  // Kevin, 2026-09-05: "yes, and add UI". Until now the page could NAME a stray registration and
  // do nothing about it, which is the "pending item that names its fix without offering it" trap
  // this codebase has hit before.
  //
  // It clears THIS NUMBER'S pointer, not the site. That distinction is the whole safety of it:
  // site 10802 is shared — Lyons Communications' own +17208934175 is live against it — and
  // deleting the site would strip 911 from a working customer number. The API refuses outright on
  // any DID that has a subscriber, so the live one cannot be reached from here even by mistake.
  const clearE911 = useMutation({
    mutationFn: () => api.delete(`/dids/${encodeURIComponent(rawE164)}/e911`),
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['did', rawE164, 'commio-live'] });
      setConfirmClearE911(false);
    },
  });
  // The address and caller-ID writes go to the carrier first and Verso second, so a
  // failure means nothing changed anywhere — see routes/dids.js.
  const setLoc = useMutation({
    mutationFn: (locationId) => api.put(`/dids/${encodeURIComponent(rawE164)}/location`, {
      location_id: locationId === '' || locationId == null ? null : Number(locationId),
    }),
    onSuccess: () => { invalidate(); queryClient.invalidateQueries({ queryKey: ['locations'] }); close(); },
  });
  const setPbx = useMutation({
    // The URL segment 'null' clears the pointer.
    mutationFn: (agentId) => api.put(`/dids/${encodeURIComponent(rawE164)}/pbx/${encodeURIComponent(agentId || 'null')}`),
    onSuccess: () => { invalidate(); close(); },
  });
  // Inactive is a state, not a deletion: the row and its history stay, so it's
  // reversible and doesn't ask anyone to type a word.
  const setStatus = useMutation({
    mutationFn: (status) => api.put(`/dids/${encodeURIComponent(rawE164)}/status`, { status }),
    onSuccess: () => { invalidate(); setConfirmInactive(false); },
  });

  const breadcrumb = (
    <div className="text-sm text-gray-500">
      <Link to={back.href} className="text-blue-600 hover:underline">{back.label}</Link>
      <span className="mx-2">/</span>
      <span className="font-mono">{fmtDid(did?.e164) || rawE164}</span>
    </div>
  );

  if (didQ.isLoading) {
    return <div className="space-y-6">{breadcrumb}<LoadingSpinner message="Loading number…" /></div>;
  }
  if (didQ.error) {
    return (
      <div className="space-y-6">
        {breadcrumb}
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {didQ.error?.body?.error || didQ.error.message || 'Failed to load DID'}
        </div>
      </div>
    );
  }
  if (!did) {
    return (
      <div className="space-y-6">
        {breadcrumb}
        <div className="rounded-md border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
          DID not found.
        </div>
      </div>
    );
  }

  const pbxAgentId    = did.pbxAgentId ?? did.pbx_agent_id ?? null;
  // Which line this number belongs to. Both the host and the 911 address are the
  // LINE's to set now, and this number inherits them, so the two cards below link
  // there instead of editing here.
  const lineId        = did.lineId ?? did.line_id ?? null;
  const commioRouteId = did.commioRouteId ?? did.commio_route_id ?? null;
  const versoLocId    = did.commioLocationId ?? did.commio_location_id ?? null;
  // The carrier's answer wins where we have one: this value is what a PSAP would be given.
  const liveE911      = liveQ.data?.e911 ?? null;
  const carrierLocId  = liveE911?.readable ? (liveE911.account_location_id ?? null) : null;
  const commioLocId   = carrierLocId ?? versoLocId;
  // OUR RECORD SAYS NOTHING AND THE CARRIER SAYS SOMETHING. Not the same as `e911Drift` below,
  // which compares two known sites; this is the case where Verso believes there is no 911 record
  // at all — the state a failed build leaves behind, and invisible until now.
  const e911Unrecorded = Boolean(liveE911?.registered) && versoLocId == null
    ? { carrierSite: liveE911.account_location_id, address: liveE911.address ?? null }
    : null;
  const routeSummary  = did.routeSummary || did.route_summary || null;
  const features      = did.features || {};
  const featuresRaw   = features.raw || {};
  const subName       = did.subscriberName || (subId ? `#${subId}` : null);
  const subType       = did.subscriberType ?? did.subscriber_type ?? null;
  // NULL caller-id id means "follows the subscriber" — the default — not "no name".
  const isInactive    = (did.status || 'active') === 'inactive';
  // A number nobody holds. Not the same as "not built": the pool is where numbers live
  // before they are sold, and that is a complete, correct state rather than work half done.
  const inInventory   = !subId && !lineId;

  const locsAll = Array.isArray(locsQ.data) ? locsQ.data
    : Array.isArray(locsQ.data?.rows) ? locsQ.data.rows
    : Array.isArray(locsQ.data?.locations) ? locsQ.data.locations : [];
  // THE LINE'S ADDRESS, NOT THE CARRIER'S POINTER.
  //
  // This resolved the address by matching `did.commioLocationId` — the carrier's 911 site for
  // this NUMBER — against the org's locations. When the line's address has moved since the last
  // 911 write, that matches a DIFFERENT location row, and every conclusion below is then drawn
  // about a row the line does not use.
  //
  // Live on Carol Marra's +13038236610 (2026-08-18). Three rows exist at 920 McCall Alley
  // (8:residential:10628, 63:residential:10627, 141:business:21110). The DID still pointed at
  // site 10628, so the page resolved row 8 and reported "this number dispatches 911 to an
  // address which belongs to another subscriber — a 911 call from it would send help to the
  // wrong address" and "the address is registered as residential while Lee Hall is a business
  // subscriber". Both false: the line's location is row 141, owned by Lee Hall, business, and
  // matching his business subscriber record. And 10628 is 920 McCall Alley too, so nobody was
  // ever going to be sent anywhere wrong.
  //
  // The address the number USES comes from the line. What the carrier holds is a separate fact,
  // and where the two disagree that is real drift worth naming — see `e911Drift` below — rather
  // than grounds for inventing a wrong address.
  const lineLocId = did.lineLocationId ?? did.line_location_id ?? null;
  const address = (lineLocId != null
      && locsAll.find((l) => Number(l.id) === Number(lineLocId)))
    || (commioLocId != null
      ? locsAll.find((l) => (l.commioLocationId ?? l.commio_location_id) === commioLocId) || null
      : null);

  // The carrier's 911 record for this number points somewhere other than its line's address.
  // A build reconciles it (`set-e911-site`); until then the two disagree and saying so is the
  // whole point. Only claimed when both sides are known — an absent pointer is "never written",
  // which the badge already covers.
  const lineLocCommioId = did.lineLocationCommioId ?? did.line_location_commio_id ?? null;
  const e911Drift = commioLocId != null && lineLocCommioId != null
    && Number(commioLocId) !== Number(lineLocCommioId)
    ? { carrierSite: commioLocId, lineSite: lineLocCommioId }
    : null;

  const subs = Array.isArray(subsQ.data) ? subsQ.data
    : Array.isArray(subsQ.data?.rows) ? subsQ.data.rows : [];
  const pbxHosts = Array.isArray(pbxQ.data) ? pbxQ.data
    : Array.isArray(pbxQ.data?.rows) ? pbxQ.data.rows : [];

  // Addresses this number may dispatch to: its subscriber's own, and only ones the
  // carrier already knows (no commio_location_id = nothing for a PSAP to
  // reference). The API enforces both; the picker doesn't offer what it'd refuse.
  const subAddresses = subId
    ? locsAll.filter((l) => locSubscriberIds(l).includes(Number(subId)))
    : [];
  // Only an address the CARRIER already holds can be a dispatch address — with no
  // commio_location_id there is nothing for a PSAP to reference, and the API
  // refuses it. But hiding the others made the picker lie: Lyons Fire has two
  // addresses and the modal offered one, which reads as "the other doesn't exist"
  // instead of "the carrier hasn't been told about it yet". Show both; make the
  // unusable one say what to do.
  const addressChoices = subAddresses.filter(
    (l) => (l.commioLocationId ?? l.commio_location_id) != null,
  );
  const unregisteredAddresses = subAddresses.filter(
    (l) => (l.commioLocationId ?? l.commio_location_id) == null,
  );

  const host = pbxHosts.find((p) => p.agent_id === pbxAgentId) || null;
  const seen = seenLabel(host?.last_seen ?? host?.lastSeen ?? host?.last_seen_at);

  const erlId = address?.erlId ?? address?.erl_id ?? null;
  // The carrier site this number's 911 record actually points at.
  const siteId = address?.commioLocationId ?? address?.commio_location_id ?? commioLocId;
  const carrierHost = commioRouteId != null ? hostByRouteId[String(commioRouteId)] : null;

  const subMatches = subSearch.trim()
    ? subs.filter((s) => `${s.name || s.display_name || ''} ${s.billingAccount || s.billing_account || ''}`
        .toLowerCase().includes(subSearch.trim().toLowerCase()))
    : subs;

  // ---- sanity checks: the pair, and the two systems that carry the number ----
  const problems = [];
  // THE ONE TRUE FINDING where the two 911 records disagree. Named instead of the two false
  // alarms this used to produce: the carrier's site for the number and the line's address are
  // both real, and a build (`set-e911-site`) reconciles them. Amber, not red — responders are
  // being sent to a registered address either way, which is why "would send help to the wrong
  // address" was the wrong claim.
  if (e911Unrecorded) {
    problems.push({ level: subId ? 'warn' : 'error', body: (
      <>The carrier has a <strong>live 911 registration</strong> for this number — site{' '}
        <strong>#{e911Unrecorded.carrierSite}</strong>
        {e911Unrecorded.address && <> ({e911Unrecorded.address})</>} — and Verso has no record of
        it. {subId
          ? <>Building this line points the carrier at this line&rsquo;s address.</>
          : <>This number is in inventory, so responders would be sent to an address it no longer
            serves. Clear it before the number is sold again.</>}
        {/* THE GESTURE, BESIDE THE PROBLEM. Naming a fault and offering nothing to do about it is
            the trap this codebase keeps rediscovering — and until now the only way to clear a
            stray registration was an API call.

            Offered ONLY for a number nobody holds. The API refuses on an assigned DID ("would
            leave a live number with nowhere to send responders") and that refusal is right, so
            the button is not shown where it would only produce it. */}
        {!subId && canAdm && (
          <span className="ml-2 inline-block">
            {confirmClearE911 ? (
              <>
                <button
                  type="button"
                  disabled={clearE911.isPending}
                  onClick={() => clearE911.mutate()}
                  className="cursor-pointer rounded-md bg-red-700 px-2 py-0.5 text-xs font-medium text-white hover:bg-red-800 disabled:cursor-wait disabled:opacity-70"
                >
                  {clearE911.isPending ? 'Clearing at the carrier…' : 'Yes, clear it'}
                </button>
                <button
                  type="button"
                  onClick={() => { setConfirmClearE911(false); clearE911.reset(); }}
                  className="ml-1.5 cursor-pointer text-xs underline hover:no-underline"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => { clearE911.reset(); setConfirmClearE911(true); }}
                className="cursor-pointer rounded-md border border-current px-2 py-0.5 text-xs font-medium hover:bg-white/40"
              >
                Clear the 911 registration
              </button>
            )}
          </span>
        )}
        {clearE911.isError && (
          // The carrier's own words, not ours: this refuses for reasons worth reading.
          <div className="mt-1 text-xs">
            {clearE911.error?.body?.error || clearE911.error?.message || 'That could not be cleared.'}
          </div>
        )}
        {/* SAID EXPLICITLY, because the site is shared and an operator has every right to worry
            about it: +17208934175 is registered against this same site and is in service. */}
        {!subId && confirmClearE911 && !clearE911.isPending && (
          <div className="mt-1 text-xs">
            This detaches <strong>this number</strong> from site #{e911Unrecorded.carrierSite}. The
            site itself, and any other number registered against it, are not touched.
          </div>
        )}
      </>
    ) });
  }
  if (e911Drift) {
    problems.push({ level: 'warn', body: (
      <>The carrier&rsquo;s 911 record for this number points at site <strong>#{e911Drift.carrierSite}</strong>,
        while this line&rsquo;s address is registered as site <strong>#{e911Drift.lineSite}</strong>. Building
        the line points the carrier at the line&rsquo;s address.</>
    ) });
  }
  if (subId && address && !locSubscriberIds(address).includes(Number(subId))) {
    problems.push({ level: 'bad', body: (
      <>This number dispatches 911 to <strong>{addressLabel(address)}</strong>, which belongs to another
        subscriber — not {subName}. A 911 call from it would send help to the wrong address. Set it to one
        of {subName}&rsquo;s.</>
    ) });
  }
  if (!subId && address) {
    // 62 of this org's 164 numbers are here, and they are NOT leftovers: the
    // address is a live location with tenants, so the likeliest truth is that the
    // number serves whoever is there and Verso never recorded the link. Amber, not
    // red — and name them, so fixing it is one click rather than a search.
    const tenants = (address.subscribers || []).filter((x) => x?.id != null);
    problems.push({ level: 'warn', body: (
      <>The carrier dispatches 911 for this number to <strong>{addressLabel(address)}</strong>, but Verso
        has it assigned to nobody.
        {tenants.length
          ? <> If it serves {tenants.length === 1 ? 'the subscriber' : 'a subscriber'} at that address, assign it:{' '}
            {tenants.map((t, i) => (
              <span key={t.id}>
                {i > 0 && ', '}
                <button
                  className="font-medium underline hover:no-underline"
                  disabled={assignSub.isPending}
                  onClick={() => assignSub.mutate(t.id)}
                >
                  {t.name || `#${t.id}`}
                </button>
              </span>
            ))}.
          </>
          : <> Assign it to a subscriber, or clear the address.</>}
      </>
    ) });
  }
  if (subId && address && subType && address.type && subType !== address.type) {
    problems.push({ level: 'warn', body: (
      <>The address is registered as <strong>{address.type}</strong> while {subName} is
        a <strong>{subType}</strong> subscriber. The carrier requires the 911 record and the caller-ID type
        to agree, so one of the two needs changing.</>
    ) });
  }
  if (carrierHost && pbxAgentId && carrierHost !== pbxAgentId) {
    problems.push({ level: 'bad', body: (
      <>The carrier delivers this number to <strong>{pbxName(carrierHost)}</strong>, but Verso has it
        on <strong>{pbxName(pbxAgentId)}</strong>. Calls arrive at the first while everything Verso builds
        goes to the second, so inbound calls fail. Set the host again to repoint the carrier with it.</>
    ) });
  }
  // ONLY ONCE IT IS SOMEBODY'S NUMBER. A number sitting in inventory has no line, and the
  // host comes from the line, so "no PBX host is recorded here" is the expected state — not
  // a fault. It was the loudest thing on the page for every unassigned number in the pool,
  // which is most of them (Kevin, 2026-08-14: "the copy on that page adds confusion").
  //
  // Once the number IS assigned, the same silence would be wrong: then a missing host means
  // provisioning has nowhere to act.
  if (carrierHost && !pbxAgentId && subId) {
    problems.push({ level: 'warn', body: (
      <>No PBX host is recorded here, though the carrier delivers this number
        to <strong>{pbxName(carrierHost)}</strong>. Record it so provisioning acts on the right host.</>
    ) });
  }

  // ---- one health verdict per card ----
  //
  // Each is "is it linked, and is what it points at in good order?" — the same
  // question for all three, answered from what the card already shows.
  const hostTone = (() => {
    if (!pbxAgentId) return carrierHost
      ? { tone: 'warn', why: `The carrier delivers this number to ${pbxName(carrierHost)}, but no host is recorded here` }
      : { tone: 'empty' };
    if (carrierHost && carrierHost !== pbxAgentId) {
      return { tone: 'bad', why: `Calls arrive at ${pbxName(carrierHost)} while Verso builds on ${pbxName(pbxAgentId)}` };
    }
    if (host && host.status && host.status !== 'active') return { tone: 'warn', why: `This host is ${host.status}` };
    if (seen.cls.includes('red')) return { tone: 'bad', why: `The agent hasn’t checked in — ${seen.text}` };
    if (seen.cls.includes('amber')) return { tone: 'warn', why: `The agent is quiet — ${seen.text}` };
    return { tone: 'ok', why: 'Host is up and the carrier delivers here' };
  })();

  const subTone = (() => {
    if (!subId) return { tone: 'empty' };
    const st = did.subscriberStatus ?? did.subscriber_status ?? null;
    if (st === 'cancelled') return { tone: 'bad', why: `${subName} is cancelled, but still holds this number` };
    if (st && st !== 'active') return { tone: 'warn', why: `${subName} is ${st}` };
    if (!did.cnam) return { tone: 'warn', why: 'No caller ID — calls from this number show the bare number' };
    return { tone: 'ok', why: `${subName} is active` };
  })();

  const locTone = (() => {
    if (!address) {
      if (features.e911) return { tone: 'warn', why: 'The carrier holds an address Verso can’t match to a location' };
      return subId
        ? { tone: 'bad', why: 'No 911 address — a 911 call from this number has nowhere to go' }
        : { tone: 'empty' };
    }
    if (!locSubscriberIds(address).includes(Number(subId))) {
      return { tone: 'bad', why: 'This address belongs to another subscriber' };
    }
    // THE SAME `erlID` BUG AS THE BADGE BELOW, and this one was louder: `!erlId` ambered the
    // 911 card on EVERY residential number, because residential sites never carry an ERL
    // (ticket #2, 2026-07-22). Lyons is almost entirely residential, so the warning that
    // matters was permanently on for nearly every DID — which trains an operator to ignore it.
    // Reaching here means `address` resolved from the DID's own carrier site, so a null
    // `siteId` should be impossible; kept as an explicit guard rather than deleted so the rule
    // is stated where the next reader looks.
    if (siteId == null) {
      return { tone: 'warn', why: 'No carrier site backs this address, so 911 has nothing to dispatch against' };
    }
    if (subType && address.type && subType !== address.type) {
      return { tone: 'warn', why: `Address is ${address.type} while the subscriber is ${subType}` };
    }
    if (address.status && address.status !== 'active') return { tone: 'warn', why: `This address is ${address.status}` };
    return { tone: 'ok', why: '911 is registered to this address' };
  })();

  // Assignment is the state of a number in service. `dids.status` used to say
  // 'active' about all 164 rows because nothing wrote it; Mark Inactive does, so an
  // inactive number can say so and it means something.
  const state = isInactive
    ? { label: 'Inactive', cls: 'bg-gray-200 text-gray-700', dot: 'bg-gray-500', tip: 'Out of service. The carrier still owns the number.' }
    : subId
      ? { label: 'Assigned', cls: 'bg-green-100 text-green-700', dot: 'bg-green-500', tip: `Assigned to ${subName}` }
      // Indigo, not a third shade of grey. "Inactive" and "Unassigned" were two
      // near-identical greys meaning opposite things — one is out of service, the
      // other is stock we can sell today — and telling them apart mattered enough
      // to give inventory its own colour.
      : { label: 'In inventory', cls: 'bg-indigo-100 text-indigo-700', dot: 'bg-indigo-500', tip: 'In inventory — we hold this number and no subscriber has it yet' };

  const mutError = (m, fallback) => m.isError && (
    <p className="mt-3 text-sm text-red-600">{m.error?.body?.error || m.error?.message || fallback}</p>
  );

  const carrierRows = [
    ['Carrier', 'Commio'],
    ['Routing profile', routeSummary],
    ['Commio DID ID', did.commioDidId && `#${did.commioDidId}`],
    ['Route ID', (commioRouteId || featuresRaw.route_id) && `#${commioRouteId || featuresRaw.route_id}`],
    ['Location ID', commioLocId && `#${commioLocId}`],
    ['Caller-ID profile', featuresRaw.caller_id && `#${featuresRaw.caller_id}`],
    ['911 registration', erlId],
    ['Rate centre', featuresRaw.rate_center || featuresRaw.rateCenter],
    ['State', featuresRaw.state],
    ['OCN', featuresRaw.ocn],
    ['LATA', featuresRaw.lata],
    ['STIR/SHAKEN', featuresRaw.stir_shaken],
    ['Trunk group', featuresRaw.trunk_group],
    ['Texting', features.sms ? 'enabled at the carrier' : 'not enabled'],
  ];

  return (
    <div className="space-y-6">
      {breadcrumb}

      {/* What the number IS, and the three things you can do to the number itself. */}
      <div className="rounded-lg border border-gray-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-mono text-xl font-bold text-gray-900">{fmtDid(did.e164)}</h1>
              <span
                title={state.tip}
                className={`inline-flex cursor-help items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${state.cls}`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${state.dot}`} />
                {state.label}
              </span>
            </div>
            <div className="mt-2 text-sm">
              <span title={TIPS.carrier} className="cursor-help text-xs font-medium uppercase tracking-wide text-gray-500">Carrier</span>
              <span className="ml-2 text-gray-900">Commio</span>
              {routeSummary
                ? <span className="ml-1 font-mono text-xs text-gray-500">{routeSummary}</span>
                : <span className="ml-1 text-xs text-gray-400">no route</span>}
            </div>
          </div>
          <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-2">
            <button className={plainBtn} onClick={() => setOpen('carrier')}>View Carrier Record</button>
            {canAdm && (
              <button
                disabled
                title="Handing the number back to Commio isn’t built yet — release it in the carrier portal for now"
                className={plainBtn}
              >
                Release to Carrier
              </button>
            )}
            {canAdm && (
              <button
                onClick={() => { setConfirmInactive(true); setStatus.reset(); }}
                disabled={!isInactive && Boolean(subId)}
                title={isInactive ? 'Put this number back into service'
                  : subId ? `Unassign it from ${subName} first`
                  : 'Take this number out of service — the row and its history stay'}
                className={plainBtn}
              >
                {isInactive ? 'Mark Active' : 'Mark Inactive'}
              </button>
            )}
          </div>
        </div>
      </div>

      {problems.length > 0 && (
        <div className="space-y-2">
          {problems.map((p, i) => <Warn key={i} level={p.level}>{p.body}</Warn>)}
        </div>
      )}

      {/* ---------------- a number nobody holds ---------------- */}
      {/*
        IN INVENTORY IS THE WHOLE STORY. This page used to answer, for a number in the pool,
        three questions nobody had asked: which PBX serves it (none — it has no line), who
        the subscriber is (nobody, that is what inventory means) and what its 911 address is
        (none, for the same reason). Three cards reading "None", a paragraph explaining that
        the host and the address belong to a line that does not exist, and an amber banner
        about a PBX host — for a number whose only fact is that it is available.

        Kevin, 2026-08-14: "we need to make the page simple... Just showing the DID is in
        inventory is adequate. It would be good to have diagnostic information available but
        somewhat hidden."

        So: one sentence and the action that changes it. Everything technical is still on the
        page, under Technical details at the foot.
      */}
      {inInventory ? (
        <div className="rounded-lg border border-gray-200 bg-white p-5">
          <p className="text-sm text-gray-700">
            This number is in inventory and is not assigned to a customer.
          </p>
          {canOp && (
            <button type="button" onClick={() => setOpen('subscriber')} className={`mt-3 ${plainBtn}`}>
              Assign to a customer
            </button>
          )}
        </div>
      ) : (
      <>
      {/* Said once, here, rather than repeated on both cards. The host and the 911
          address moved to the line: everything PBX-side derives from the line's
          host, and E911 comes from the line's location, so a number showing one
          answer while its line holds another is exactly the drift the move
          removes. */}
      <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-600">
        <strong>Which PBX serves this number</strong> and <strong>its 911 address</strong> belong
        to the line, which this number inherits them from.{' '}
        {lineId && subId ? (
          <Link to={`/subscribers/${subId}#lines`} className="font-medium text-blue-600 hover:underline">
            Open the line to change them
          </Link>
        ) : (
          <span>This number has no line yet — add one on the subscriber&rsquo;s Lines tab.</span>
        )}
      </div>

      {/* The three things this number points at, in call order. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <LinkCard
          icon={<HostIcon />}
          label="PBX host"
          tip={TIPS.host}
          tone={hostTone.tone}
          toneReason={hostTone.why}
          filled={Boolean(pbxAgentId)}
          to={`/pbx-hosts/${pbxAgentId}`}
          // Read-only here. The LINE owns the host now and this number inherits it,
          // so the control lives on the line — see store/lines.js setLineHost. Editing
          // it from the number was how a host could be changed without the extension,
          // the inbound route or the carrier following, and the API refuses it now
          // regardless, so offering the control would only produce a 409.
          disabledReason={lineId
            ? 'The line owns the PBX host — set it on the line'
            : 'This number has no line yet; add one to give it a host'}
        >
          {pbxAgentId ? (
            <>
              <div className="truncate text-sm font-semibold text-gray-900" title={pbxAgentId}>{pbxName(pbxAgentId)}</div>
              {/* Is the host alive, and does the carrier actually deliver here? The
                  two things that decide whether calls to this number arrive. */}
              <div className="mt-0.5 text-xs">
                <span className={seen.cls}>{seen.text}</span>
                {host?.version && (
                  <span className="text-gray-500" title="verso-agent version on this host">
                    {' · '}<span className="font-mono">{host.version.split('+')[0]}</span>
                  </span>
                )}
              </div>
              {/* Two records, compared: the number's Commio routing profile resolves
                  to a host (agents.commio_route_id), and Verso records a host of its
                  own. Agreeing means inbound calls land where Verso builds; not
                  agreeing is why a number "on PBX-02" can ring PBX-01. */}
              {carrierHost === pbxAgentId
                ? (
                  <div className="mt-0.5 text-xs text-green-700" title={`Commio's routing profile ${routeSummary || ''} points at this same host, so calls arrive where Verso builds them`}>
                    matches carrier route
                  </div>
                )
                : carrierHost
                  ? (
                    <div className="mt-0.5 text-xs text-red-600" title={`Commio's routing profile ${routeSummary || ''} points at ${pbxName(carrierHost)}, so calls arrive there instead`}>
                      carrier routes to {pbxName(carrierHost)}
                    </div>
                  )
                  : null}
            </>
          ) : (
            <div
              className="text-sm text-gray-500"
              title={routeSummary && !carrierHost
                ? `The carrier's profile “${routeSummary}” isn't one of this org's PBX hosts, so the host can't be derived from it`
                : undefined}
            >
              None{carrierHost ? <span className="text-red-600"> · carrier routes to {pbxName(carrierHost)}</span> : ''}
            </div>
          )}
        </LinkCard>

        <LinkCard
          icon={<PersonIcon />}
          label="Subscriber"
          tip={TIPS.subscriber}
          tone={subTone.tone}
          toneReason={subTone.why}
          filled={Boolean(subId)}
          to={`/subscribers/${subId}`}
          onOpen={canOp ? () => setOpen('subscriber') : undefined}
          onEdit={canOp ? () => setOpen('subscriber') : undefined}
          editLabel="Change subscriber"
          openLabel="Assign a subscriber"
          disabledReason={!canOp ? 'You don’t have permission to assign this number' : undefined}
        >
          {subId ? (
            <>
              <div className="truncate text-sm font-semibold text-gray-900">{subName}</div>
              <div className="mt-0.5 text-xs text-gray-500">
                {subType && <span className="capitalize">{subType}</span>}
                {did.routedExtension && <>{subType ? ' · ' : ''}rings <span className="font-mono">{did.routedExtension}</span></>}
              </div>
            </>
          ) : (
            <div className="text-sm text-gray-500">None</div>
          )}
        </LinkCard>

        <LinkCard
          icon={<PinIcon />}
          label="Location"
          tip={TIPS.location}
          tone={locTone.tone}
          toneReason={locTone.why}
          filled={Boolean(address)}
          to={address ? `/locations/${address.id}` : '#'}
          // Read-only for the same reason as the host: E911 comes from the LINE's
          // location, so a number showing one address while its line claims another
          // is a 911 answer nobody can trust. One place to set it, on the line.
          disabledReason={lineId
            ? 'The line owns the 911 address — set it on the line'
            : 'This number has no line yet; add one to give it an address'}
          editLabel="Change 911 address"
          openLabel="Set the 911 address"
          disabledReason={!subId ? 'Assign a subscriber first — a number dispatches to one of their addresses'
            : !canAdm ? 'You don’t have permission to set the 911 address' : undefined}
        >
          {address ? (
            <>
              <div className="truncate text-sm font-semibold text-gray-900">{addressLine1(address)}</div>
              <div className="mt-0.5 text-xs text-gray-500">
                {addressRest(address)}
                {/* THE LINKED CARRIER SITE, NOT `erlID`.
                    This is ticket #2's bug in a third place. `erlID` is a separate
                    enterprise-ERL concept and RESIDENTIAL SITES NEVER CARRY ONE, so keying off
                    it reported every residential address as unregistered — corrected in
                    `getE911StatusMap` and `handleGetLocationE911` on 2026-07-22 and missed
                    here. Found live on +13038235285 (2026-08-18): this page said "911 not
                    registered yet" while the location page and the line page both said
                    "registered as site #10653", and pressing Register on the strength of it
                    would have made a SECOND carrier site for an address that already had one.
                    On THIS page the signal is unusually strong: `address` is resolved FROM the
                    DID's own `commioLocationId`, so having one means this number's E911 record
                    at the carrier points at that site — which is the fact that decides where
                    responders go. `erlID` stays as detail when it happens to exist. */}
                {siteId != null
                  ? (
                    <span
                      className="ml-1 text-green-700"
                      title={`This number dispatches 911 to carrier site #${siteId}${erlId ? ` (ERL ${erlId})` : ''}`}
                    >
                      · 911 registered
                    </span>
                  )
                  : <span className="ml-1 text-amber-800" title="No carrier site backs this address, so a 911 call would be dispatched to whatever the carrier last held for this number">· 911 not registered yet</span>}
              </div>
            </>
          ) : features.e911 ? (
            <div className="text-sm text-amber-800" title="The carrier holds an address for this number that Verso can’t match to a location">
              Unmatched carrier address
            </div>
          ) : subId ? (
            <div className="text-sm text-red-600" title="A 911 call from this number has nowhere to go">None</div>
          ) : (
            <div className="text-sm text-gray-500">None</div>
          )}
        </LinkCard>
      </div>

      {/* Caller ID is a setting of the NUMBER — its own card, at the same level as the
          number's other settings. Same component the line page uses, so the two screens
          cannot drift into describing the override differently. */}
      {/* `??` IS WRONG FOR THESE TWO, AND IT COST THE WHOLE WARNING.
          They read `did.subscriberCallerIdId ?? did.subscriber_caller_id_id` — a
          harmless-looking camelCase/snake_case fallback. But the payload sends
          `subscriberCallerIdId: null`, `??` treats null as nullish, and the snake_case key
          does not exist — so a meaningful `null` arrived as `undefined`.

          CallerIdCard separates the two on purpose: `null` means "the customer has no
          profile either", `undefined` means "the caller did not tell me, so stay quiet".
          It stayed quiet, and printed the reassuring “Follows Art Caruso’s name” on a
          number with no carrier profile recorded anywhere — while the line page's pending
          banner said the opposite (Kevin, 2026-08-17). Two screens, one row, opposite
          stories, and the disagreement was a `??`.

          `pick` falls back only when the camelCase key is genuinely ABSENT, which is all
          the fallback was ever for. */}
      <CallerIdCard
        e164={rawE164}
        cnam={did.cnam}
        cnamType={address?.type || did.subscriberType || did.subscriber_type || null}
        subscriberCallerIdId={pick(did, 'subscriberCallerIdId', 'subscriber_caller_id_id')}
        callerIdId={pick(did, 'commioCallerIdId', 'commio_caller_id_id')}
        subscriberId={subId}
        subscriberName={subName}
        canEdit={canAdm}
        onSaved={() => invalidate()}
      />
      </>
      )}

      {/* ---------------- the technical answer, for when somebody wants it ----------------
        Present on every number, shut on every number. An operator answering "is this number
        free?" never opens it; an operator working out why a call went somewhere strange has
        it one click away instead of in another system.
      */}
      <details className="rounded-lg border border-gray-200 bg-white">
        <summary className="cursor-pointer px-5 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50">
          Technical details
        </summary>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 border-t border-gray-100 px-5 py-4 text-sm sm:grid-cols-2">
          <TechRow label="Carrier routing profile">
            {routeSummary || <span className="text-gray-400">none</span>}
          </TechRow>
          <TechRow label="Carrier delivers to">
            {carrierHost
              ? pbxName(carrierHost)
              : <span className="text-gray-400">not one of this org&rsquo;s hosts</span>}
          </TechRow>
          <TechRow label="PBX host recorded here">
            {pbxAgentId
              ? <Link to={`/pbx-hosts/${pbxAgentId}`} className="text-blue-600 hover:underline">{pbxName(pbxAgentId)}</Link>
              : <span className="text-gray-400">none — it comes from the line</span>}
          </TechRow>
          <TechRow label="Line">
            {lineId
              ? <Link to={`/lines/${lineId}`} className="text-blue-600 hover:underline">#{lineId}</Link>
              : <span className="text-gray-400">none</span>}
          </TechRow>
          <TechRow label="Rings extension">
            {did.routedExtension
              ? <span className="font-mono text-xs">{did.routedExtension}</span>
              : <span className="text-gray-400">none</span>}
          </TechRow>
          <TechRow label="Carrier 911 site">
            {commioLocId ? <span className="font-mono text-xs">#{commioLocId}</span> : <span className="text-gray-400">none</span>}
          </TechRow>
          <TechRow label="E.164">
            <span className="font-mono text-xs">{rawE164}</span>
          </TechRow>
        </dl>
      </details>

      {/* ---------------- pickers ---------------- */}

      {open === 'subscriber' && (
        <Modal
          title={subId ? 'Subscriber' : 'Assign this number to a subscriber'}
          subtitle="Search by name or billing account."
          onClose={() => { close(); assignSub.reset(); }}
        >
          {subId && (
            <>
              <div className="mt-4 flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                <span className="min-w-0 text-sm text-gray-700">
                  Currently <span className="font-medium text-gray-900">{subName}</span>
                </span>
                <button
                  className={dangerBtn}
                  disabled={unassign.isPending}
                  onClick={() => { close(); setConfirmUnassign(true); unassign.reset(); }}
                >
                  Unassign
                </button>
              </div>

              {/* Caller ID USED TO LIVE HERE, inside the customer relationship, under a
                  comment saying it belonged with the subscriber. It does not: the override
                  is `dids.commio_caller_id_id`, per number. Nesting it here made a
                  per-number setting read as an account-level one, which is how it was
                  read. It is now its own card below, shared with the line page. */}

              <p className="mt-4 text-xs font-medium uppercase tracking-wide text-gray-500">Move to another subscriber</p>
            </>
          )}
          <input
            type="text"
            autoFocus
            value={subSearch}
            onChange={(e) => setSubSearch(e.target.value)}
            placeholder="Search subscribers"
            aria-label="Search subscribers"
            className="mt-4 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {mutError(assignSub, 'Assign failed')}
          <div className="mt-3 space-y-1.5">
            {subsQ.isLoading && <p className="text-sm text-gray-500">Loading subscribers…</p>}
            {!subsQ.isLoading && subMatches.length === 0 && (
              <p className="text-sm text-gray-500">No subscriber matches “{subSearch}”.</p>
            )}
            {subMatches.slice(0, 50).map((s) => (
              <button
                key={s.id}
                type="button"
                disabled={assignSub.isPending}
                onClick={() => assignSub.mutate(s.id)}
                className={pickRow}
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium text-gray-900">{s.name || s.display_name || `#${s.id}`}</span>
                  <span className="block text-xs text-gray-500">
                    <span className="capitalize">{s.type || 'subscriber'}</span>
                    {(s.didCount ?? s.did_count) != null && <> · {s.didCount ?? s.did_count} numbers</>}
                    {(s.locationCount ?? s.location_count) != null && <> · {s.locationCount ?? s.location_count} addresses</>}
                  </span>
                </span>
                <span className="flex-shrink-0 text-xs font-medium text-blue-700">
                  {assignSub.isPending ? 'Assigning…' : 'Assign'}
                </span>
              </button>
            ))}
            {subMatches.length > 50 && (
              <p className="text-xs text-gray-500">Showing the first 50 — narrow the search to see the rest.</p>
            )}
          </div>
        </Modal>
      )}

      {open === 'location' && (
        <Modal
          title="Where 911 sends help"
          subtitle={`The address a dispatcher receives for calls from ${fmtDid(did.e164)}.`}
          onClose={() => { close(); setLoc.reset(); }}
        >
          {mutError(setLoc, 'Could not set the address')}
          <div className="mt-4 space-y-1.5">
            {addressChoices.length === 0 && (
              <p className="text-sm text-amber-700">
                {subId
                  ? `${subName} has no carrier-registered address yet. Add one on their page and sync it to the carrier first — until then there is nowhere for 911 to send help.`
                  : 'Assign this number to a subscriber first — the address has to be one of theirs. You can still clear the address the carrier holds.'}
              </p>
            )}
            {addressChoices.map((l) => {
              const current = address && Number(address.id) === Number(l.id);
              return (
                <button
                  key={l.id}
                  type="button"
                  disabled={setLoc.isPending || current}
                  onClick={() => setLoc.mutate(l.id)}
                  className={pickRow}
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-gray-900">{addressLine1(l)}</span>
                    <span className="block text-xs text-gray-500">
                      {addressRest(l)}
                      {l.type && <> · <span className="capitalize">{l.type}</span></>}
                      {(l.erlId ?? l.erl_id) ? <span className="text-green-700"> · 911 registered</span>
                        : <span className="text-amber-800"> · not registered yet</span>}
                    </span>
                  </span>
                  <span className="flex-shrink-0 text-xs font-medium text-blue-700">
                    {current ? 'Current' : setLoc.isPending ? 'Saving…' : 'Use this'}
                  </span>
                </button>
              );
            })}

            {/* Present but unusable, with the reason and the fix. Silently omitting
                these is what made the modal look like it had lost an address. */}
            {unregisteredAddresses.map((l) => (
              <div key={l.id} className="flex items-start justify-between gap-3 rounded-md border border-dashed border-gray-300 px-3 py-2">
                <span className="min-w-0">
                  <span className="block truncate font-medium text-gray-500">{addressLine1(l)}</span>
                  <span className="block text-xs text-gray-500">
                    {addressRest(l)}
                    {l.type && <> · <span className="capitalize">{l.type}</span></>}
                    <span className="text-amber-800"> · not with the carrier yet</span>
                  </span>
                </span>
                <Link
                  to={`/locations/${l.id}`}
                  className="flex-shrink-0 text-xs font-medium text-blue-700 hover:underline"
                  title="911 can only dispatch to an address the carrier holds. Register it on the address page, then come back."
                >
                  Register it
                </Link>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-gray-600">
            This goes to the carrier as an order and takes effect once they accept it.
          </p>
          {address && (
            <button
              className={`${plainBtn} mt-3`}
              disabled={setLoc.isPending}
              onClick={() => setLoc.mutate('')}
            >
              Remove the 911 address
            </button>
          )}
        </Modal>
      )}

      {open === 'host' && (
        <Modal title="PBX host" subtitle="Which host the carrier delivers this number to." onClose={() => { close(); setPbx.reset(); }}>
          <label htmlFor="did-pbx" className="mt-4 block text-xs font-medium text-gray-700">PBX host</label>
          <select
            id="did-pbx"
            value={pickedPbx}
            onChange={(e) => setPickedPbx(e.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">— clear —</option>
            {pbxHosts.map((p) => (
              <option key={p.agent_id} value={p.agent_id}>
                {p.name || p.agent_id}{p.commio_route_id ? '' : ' — no carrier route'}
              </option>
            ))}
          </select>
          <p className={`mt-2 text-xs ${pickedPbx ? 'text-gray-600' : 'text-red-700'}`}>
            {pickedPbx
              ? 'Saving also points the carrier’s inbound routing profile at this host, so calls follow the number.'
              : 'Clearing also removes the inbound voice route at the carrier. Inbound calls to this number will fail at Commio until something routes it again.'}
          </p>
          {mutError(setPbx, 'Save failed')}
          <div className="mt-4 flex items-center gap-2">
            <button className={primaryBtn} disabled={setPbx.isPending} onClick={() => setPbx.mutate(pickedPbx)}>
              {setPbx.isPending ? 'Saving…' : 'Save'}
            </button>
            <button className={plainBtn} onClick={() => { close(); setPbx.reset(); }}>Cancel</button>
          </div>
        </Modal>
      )}

      {open === 'carrier' && (
        <Modal
          wide
          title="Carrier record"
          subtitle={`What Commio holds for ${fmtDid(did.e164)}. Reference for troubleshooting — nothing here is set from this panel.`}
          onClose={close}
        >
          <dl className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
            {carrierRows.map(([label, value]) => (
              <div key={label}>
                <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
                <dd className="mt-0.5 font-mono text-xs text-gray-900">
                  {value || <span className="font-sans text-gray-300">—</span>}
                </dd>
              </div>
            ))}
          </dl>
          <p className="mt-4 text-xs text-gray-500">
            What this number rings, and whether it can text, are set on the subscriber
            {subId && <> — see <Link to={`/subscribers/${subId}`} className="text-blue-600 hover:underline">{subName}</Link></>}.
          </p>
        </Modal>
      )}

      {confirmUnassign && (
        <ConfirmDeleteDialog
          title="Unassign DID"
          body={`Return ${fmtDid(did.e164)} to inventory and clear the name it presents.${
            address
              ? ` Its 911 address (${addressLabel(address)}) stays on the number until you change it — clear it too if this number is going back into stock.`
              : ''
          } This does not release it from Commio.`}
          confirmLabel="Unassign"
          destructive={false}
          onConfirm={() => unassign.mutate()}
          onCancel={() => { setConfirmUnassign(false); unassign.reset(); }}
          isPending={unassign.isPending}
          error={unassign.isError ? (unassign.error?.body?.error || unassign.error?.message || 'Unassign failed') : null}
        />
      )}
      {confirmInactive && (
        <ConfirmDeleteDialog
          title={isInactive ? 'Mark active' : 'Mark inactive'}
          body={isInactive
            ? `Put ${fmtDid(did.e164)} back into service. It becomes assignable again.`
            : `Take ${fmtDid(did.e164)} out of service. The row and its history stay, and Commio still owns the number — this is not a release. You can mark it active again at any time.`}
          confirmLabel={isInactive ? 'Mark Active' : 'Mark Inactive'}
          destructive={false}
          onConfirm={() => setStatus.mutate(isInactive ? 'active' : 'inactive')}
          onCancel={() => { setConfirmInactive(false); setStatus.reset(); }}
          isPending={setStatus.isPending}
          error={setStatus.isError ? (setStatus.error?.body?.error || setStatus.error?.message || 'Could not change the status') : null}
        />
      )}
    </div>
  );
}
